import { useEffect, useMemo, useState } from 'react';

/** A player annotated for a given week by the backend (NFL No-Repeat). */
export interface AvailablePlayer {
  playerId: string;
  playerName: string;
  position: string;
  teamId: string;
  teamName: string | null;
  teamShortName: string | null;
  opponentTeamId: string | null;
  opponent: string | null; // opponent short name for this week (null = bye/no game)
  isHome: boolean | null;
  kickoff: string | null;
  used: boolean; // burned in another week
  locked: boolean; // his game this week has started
  available: boolean;
}

/** "vs BAL" / "@ KC" for this week, or a bye warning when the team has no game. */
export function matchupLabel(p: AvailablePlayer): string {
  if (!p.opponent) return '· BYE';
  return `${p.isHome ? 'vs' : '@'} ${p.opponent}`;
}

/**
 * Live lock check: the server flag is authoritative at load time, but if the
 * page stays open across a kickoff we also compare the kickoff timestamp to
 * now so slots lock in the UI the moment the game starts.
 */
export function isLockedNow(p: AvailablePlayer): boolean {
  return p.locked || (p.kickoff ? Date.now() >= new Date(p.kickoff).getTime() : false);
}

export interface PickerOption {
  player: AvailablePlayer;
  disabled: boolean;
  reason: string | null; // why it's disabled ("used Wk 3", "kicked off", "already in lineup")
}

interface GameGroup {
  key: string;
  label: string;
  kickoff: string | null;
  items: PickerOption[];
}

function shortKickoff(kickoff: string | null): string {
  if (!kickoff) return '';
  return new Date(kickoff).toLocaleString([], {
    weekday: 'short',
    hour: 'numeric',
    minute: '2-digit',
  });
}

interface Props {
  slotLabel: string;
  options: PickerOption[];
  currentPlayerId: string | null;
  /** null clears the slot */
  onSelect: (playerId: string | null) => void;
  onClose: () => void;
}

/**
 * Full-screen player picker for the NFL No-Repeat weekly lineup.
 * Search + team filter at the top; players grouped by this week's games in
 * kickoff order. Unavailable players (burned / kicked off / already in the
 * lineup) are hidden unless toggled on.
 */
export function NflPlayerPicker({ slotLabel, options, currentPlayerId, onSelect, onClose }: Props) {
  const [search, setSearch] = useState('');
  const [teamFilter, setTeamFilter] = useState('');
  const [showUnavailable, setShowUnavailable] = useState(false);

  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose();
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [onClose]);

  // Teams represented among this slot's eligible players, for the filter dropdown.
  const teams = useMemo(() => {
    const m = new Map<string, string>();
    for (const o of options) {
      m.set(o.player.teamId, o.player.teamShortName || o.player.teamName || '?');
    }
    return Array.from(m.entries())
      .map(([id, name]) => ({ id, name }))
      .sort((a, b) => a.name.localeCompare(b.name));
  }, [options]);

  const { groups, visibleCount } = useMemo(() => {
    const q = search.trim().toLowerCase();
    const visible = options.filter((o) => {
      if (!showUnavailable && o.disabled) return false;
      if (teamFilter && o.player.teamId !== teamFilter) return false;
      if (q) {
        const hay =
          `${o.player.playerName} ${o.player.teamShortName || ''} ${o.player.teamName || ''}`.toLowerCase();
        if (!hay.includes(q)) return false;
      }
      return true;
    });

    const byGame = new Map<string, GameGroup>();
    for (const o of visible) {
      const p = o.player;
      let key: string;
      let label: string;
      if (!p.opponent || !p.opponentTeamId) {
        key = 'bye';
        label = 'Bye week — no game';
      } else {
        // One group per game, keyed by the home team.
        key = p.isHome ? p.teamId : p.opponentTeamId;
        const own = p.teamShortName || p.teamName || '?';
        label = p.isHome ? `${p.opponent} @ ${own}` : `${own} @ ${p.opponent}`;
      }
      let g = byGame.get(key);
      if (!g) {
        g = { key, label, kickoff: p.kickoff, items: [] };
        byGame.set(key, g);
      }
      g.items.push(o);
    }

    const sorted = Array.from(byGame.values()).sort((a, b) => {
      if (a.key === 'bye') return 1;
      if (b.key === 'bye') return -1;
      const ta = a.kickoff ? new Date(a.kickoff).getTime() : Infinity;
      const tb = b.kickoff ? new Date(b.kickoff).getTime() : Infinity;
      return ta - tb || a.label.localeCompare(b.label);
    });
    for (const g of sorted) {
      g.items.sort((x, y) => x.player.playerName.localeCompare(y.player.playerName));
    }
    return { groups: sorted, visibleCount: visible.length };
  }, [options, search, teamFilter, showUnavailable]);

  return (
    <div
      className="fixed inset-0 z-50 flex items-end sm:items-center justify-center bg-black/40 p-0 sm:p-4"
      onClick={onClose}
    >
      <div
        className="bg-white w-full sm:max-w-lg sm:rounded-xl rounded-t-xl shadow-xl flex flex-col max-h-[90vh] sm:max-h-[80vh]"
        onClick={(e) => e.stopPropagation()}
      >
        {/* Header + controls (fixed; list below scrolls) */}
        <div className="p-4 border-b border-gray-200 space-y-3">
          <div className="flex items-center justify-between">
            <h3 className="font-semibold text-gray-900">Pick your {slotLabel}</h3>
            <button
              type="button"
              onClick={onClose}
              aria-label="Close"
              className="text-gray-400 hover:text-gray-600 text-xl leading-none px-1"
            >
              ✕
            </button>
          </div>
          <div className="flex gap-2">
            <input
              type="search"
              autoFocus
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder="Search players…"
              className="flex-1 min-w-0 px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 text-sm"
            />
            <select
              value={teamFilter}
              onChange={(e) => setTeamFilter(e.target.value)}
              className="w-32 px-2 py-2 border border-gray-300 rounded-lg text-sm text-gray-700 focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
              aria-label="Filter by team"
            >
              <option value="">All teams</option>
              {teams.map((t) => (
                <option key={t.id} value={t.id}>
                  {t.name}
                </option>
              ))}
            </select>
          </div>
          <div className="flex items-center justify-between">
            <label className="flex items-center gap-1.5 text-xs text-gray-600 select-none">
              <input
                type="checkbox"
                checked={showUnavailable}
                onChange={(e) => setShowUnavailable(e.target.checked)}
                className="rounded border-gray-300"
              />
              Show unavailable players
            </label>
            <span className="text-xs text-gray-400">
              {visibleCount} player{visibleCount === 1 ? '' : 's'}
            </span>
          </div>
        </div>

        {/* Player list, grouped by game in kickoff order */}
        <div className="overflow-y-auto flex-1 p-2">
          <button
            type="button"
            onClick={() => onSelect(null)}
            className="w-full text-left px-3 py-2 rounded-lg text-sm text-gray-500 hover:bg-gray-50"
          >
            — Empty (scores 0) —
          </button>

          {groups.length === 0 && (
            <p className="text-sm text-gray-500 text-center py-8">
              No players match{search ? ` “${search}”` : ''}.
            </p>
          )}

          {groups.map((g) => (
            <div key={g.key} className="mt-2">
              <div className="sticky top-0 bg-gray-50 border-y border-gray-100 px-3 py-1.5 flex items-center justify-between">
                <span className="text-xs font-semibold text-gray-700">{g.label}</span>
                <span className="text-[11px] text-gray-400">{shortKickoff(g.kickoff)}</span>
              </div>
              <ul>
                {g.items.map((o) => {
                  const p = o.player;
                  const isCurrent = p.playerId === currentPlayerId;
                  return (
                    <li key={p.playerId}>
                      <button
                        type="button"
                        disabled={o.disabled && !isCurrent}
                        onClick={() => onSelect(p.playerId)}
                        className={`w-full text-left px-3 py-2 flex items-center gap-2 rounded-lg text-sm ${
                          isCurrent
                            ? 'bg-emerald-50 text-emerald-900'
                            : o.disabled
                              ? 'text-gray-400 cursor-not-allowed'
                              : 'text-gray-800 hover:bg-blue-50'
                        }`}
                      >
                        <span className="flex-1 min-w-0 truncate">
                          <span className="font-medium">{p.playerName}</span>
                          <span className="ml-1.5 text-xs text-gray-400">
                            {p.position} · {p.teamShortName || p.teamName}
                          </span>
                        </span>
                        {o.disabled && o.reason && (
                          <span className="text-[11px] text-gray-400 flex-shrink-0">{o.reason}</span>
                        )}
                        {isCurrent && (
                          <span className="text-emerald-600 flex-shrink-0" aria-hidden>
                            ✓
                          </span>
                        )}
                      </button>
                    </li>
                  );
                })}
              </ul>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}
